import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMemberAuth, formatErr } from "./MemberAuthContext";
import { LOGO_URL } from "../assets/images";
import { CheckCircle2 } from "lucide-react";
import PasswordInput from "./PasswordInput";

export default function MemberRegister() {
  const { register, loginGoogle, member } = useMemberAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (member) navigate("/membros", { replace: true });
  }, [member, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Palavra-passe com pelo menos 8 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center p-6" data-testid="member-register-success">
        <div className="w-full max-w-md bg-white border border-[hsl(var(--primary))] p-10 sm:p-12">
          <CheckCircle2 className="h-12 w-12 text-[hsl(var(--primary))]" />
          <h1 className="display-heading text-3xl mt-6 text-[hsl(var(--primary))]">Pedido recebido.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">
            A Direção vai analisar a sua candidatura e, assim que aprovada, receberá um email a confirmar e pode entrar na área de sócios.
          </p>
          <Link to="/" className="btn-outline mt-10 inline-flex">Voltar ao site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center p-6" data-testid="member-register-page">
      <div className="w-full max-w-md bg-white border border-[hsl(var(--border))] p-10 sm:p-12">
        <a href="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="CIEES" className="h-14 w-14" />
          <div>
            <div className="display-heading text-xl text-[hsl(var(--primary))]">CIEES Portugal</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--muted-foreground))]">Pedido de adesão</div>
          </div>
        </a>

        <h1 className="display-heading text-3xl mt-10">Tornar-me sócio.</h1>
        <span className="accent-rule mt-6 block" />
        <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">
          Preencha os dados abaixo. A Direção do CIEES Portugal vai analisar o seu pedido e responder em breve.
        </p>

        <button
          type="button"
          onClick={loginGoogle}
          className="mt-8 w-full inline-flex items-center justify-center gap-3 px-5 py-3 bg-white border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm uppercase tracking-[0.16em] font-medium hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
          data-testid="member-register-google"
        >
          Continuar com Google
        </button>

        <div className="my-7 flex items-center gap-3">
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">ou</div>
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="member-register-form">
          <Field label="Nome completo">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" data-testid="member-register-name" />
          </Field>
          <Field label="Email">
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-input" data-testid="member-register-email" />
          </Field>
          <Field label="Palavra-passe (mín. 8 caracteres)">
            <PasswordInput required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="form-input" testIdPrefix="member-register-password" />
          </Field>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="member-register-submit">
            {loading ? "A enviar…" : "Enviar pedido"}
          </button>
        </form>

        <div className="mt-7 text-sm">
          <Link to="/membros/login" className="text-[hsl(var(--primary))] hover:underline">Já sou sócio — entrar →</Link>
        </div>
      </div>
      <style>{`
        .form-input { width:100%; background:white; border:1px solid hsl(var(--border)); padding:10px 12px; font-family:"Outfit", sans-serif; font-size:15px; }
        .form-input:focus { outline:none; border-color: hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">{label}</label>
      {children}
    </div>
  );
}
